import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/Header';
import { useCart } from '@/contexts/CartContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ShoppingBag, Trash2, Minus, Plus } from 'lucide-react';
import { QuickCheckoutSheet } from '@/components/checkout/QuickCheckoutSheet';
import { useState } from 'react';

export default function CartPage() {
  const { items, totalPrice, updateQuantity, removeFromCart } = useCart();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { hotelId } = useParams<{ hotelId: string }>();
  const [checkoutOpen, setCheckoutOpen] = useState(false);


  const handleCheckout = () => {
    setCheckoutOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header showBackButton title={t('cart')} showCart={false} />
      
      <main className="flex-1 overflow-y-auto pb-24 hide-scrollbar">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <ShoppingBag className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-medium mb-2">{t('emptyCart')}</h2>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {t('browseServices')}
            </p>
            <Button onClick={() => navigate(`/${hotelId}`)}>
              {t('continueShopping')}
            </Button>
          </div>
        ) : (
          <div className="p-4">
            <div className="space-y-4 mb-4">
              {items.map((item) => (
                <div key={item.product.id} className="flex border-b pb-4">
                  <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1 ml-3 flex flex-col justify-between">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-sm line-clamp-1">{item.product.name}</h3>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 -mr-1 -mt-1 text-muted-foreground"
                        onClick={() => removeFromCart(item.product.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center justify-between mt-2">
                      <p className="font-medium">{t('currency')}{item.product.price.toFixed(2)}</p>
                      
                      <div className="flex items-center border rounded">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 rounded-r-none"
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        
                        <div className="flex h-7 w-7 items-center justify-center text-xs">
                          {item.quantity}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 rounded-l-none"
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      
      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t safe-bottom">
          <div className="flex justify-between items-center mb-3">
            <span className="font-medium">{t('total')}</span>
            <span className="text-lg font-bold">{t('currency')}{totalPrice.toFixed(2)}</span>
          </div>
          <Button 
            className="w-full h-12 text-base"
            onClick={handleCheckout}
          >
            {t('proceedToCheckout')}
          </Button>
        </div>
      )}



    <QuickCheckoutSheet 
      open={checkoutOpen} 
      onOpenChange={setCheckoutOpen} 
    />
    </div>
  );
}